/** Small form controls. Typed values commit on Enter/blur; dragging a number's label scrubs it as
 * one undoable gesture. */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useEditor } from "./store";

export function Row({ label, children, hint }: { label: ReactNode; children: ReactNode; hint?: string }) {
  return (
    <label className="row" title={hint}>
      <span className="row-label">{label}</span>
      <span className="row-field">{children}</span>
    </label>
  );
}

export function NumberField({
  value,
  onChange,
  step = 0.1,
  min,
  max,
  suffix,
  label,
  digits = 2,
  hint,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
  label: string;
  digits?: number;
  hint?: string;
}) {
  const [text, setText] = useState(fmt(value, digits));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(fmt(value, digits));
  }, [value, digits]);
  const clamp = (v: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, v));
  const commit = () => {
    const v = parseFloat(text);
    if (Number.isFinite(v) && clamp(v) !== value) onChange(clamp(v));
    else setText(fmt(value, digits));
  };
  const onLabelDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const x0 = e.clientX;
    const v0 = value;
    const { begin, end } = useEditor.getState();
    begin();
    const move = (ev: PointerEvent) => {
      const k = ev.shiftKey ? 10 : ev.altKey ? 0.1 : 1;
      onChange(clamp(round(v0 + ((ev.clientX - x0) / 4) * step * k, digits)));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      end();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <div className="row" title={hint}>
      <span className="row-label scrub" onPointerDown={onLabelDown}>
        {label}
      </span>
      <span className="row-field">
        <input
          className="num"
          value={text}
          inputMode="decimal"
          onFocus={() => (focused.current = true)}
          onBlur={() => {
            focused.current = false;
            commit();
          }}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") {
              setText(fmt(value, digits));
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
        {suffix && <span className="suffix">{suffix}</span>}
      </span>
    </div>
  );
}

export function Select<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as T)}>
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}

export function Color({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <span className="color">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} />
      <code>{value}</code>
    </span>
  );
}

export function TextArea({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return <textarea value={text} rows={2} onChange={(e) => setText(e.target.value)} onBlur={() => text !== value && onChange(text)} />;
}

export function Slider({ value, min, max, step, onChange }: { value: number; min: number; max: number; step: number; onChange: (v: number) => void }) {
  return <input type="range" value={value} min={min} max={max} step={step} onChange={(e) => onChange(parseFloat(e.target.value))} />;
}

export function fmt(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return "";
  return String(round(v, digits));
}

export function round(v: number, digits = 2): number {
  const k = 10 ** digits;
  return Math.round(v * k) / k;
}

export function timecode(t: number, fps = 30): string {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  const f = Math.floor((t * fps) % fps + 1e-6);
  return `${m}:${String(s).padStart(2, "0")}.${String(f).padStart(2, "0")}`;
}
